import React, { useEffect, useId, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import { type EmailConfirmationState } from '@/features/forgot/components/EmailConfirmation';
import useFetch from '@/hooks/common/useFetch';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useToast from '@/hooks/store/useToast';
import { HANKAKU_REGEXP, SAPURI_EMAIL_REGEXP } from '@/store/constants';

import styles from './ForgotPasswordForm.module.scss';

type FormValues = {
  email: string;
};

interface ServerError {
  code?: string;
  message: string;
}

const ForgotPasswordForm: React.FC = () => {
  const intl = useIntl();
  const inputId = useId();
  const [formValues] = useState<FormValues>({
    email: '',
  });

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(
        intl.formatMessage(
          { id: 'forms.errors.required' },
          { label: 'メールアドレス' },
        ),
      )
      .test('email-format', 'メールアドレスの形式が不正です', (value) => {
        if (!value) return true;
        if (/\.$/.test(value)) return false;
        return SAPURI_EMAIL_REGEXP.test(value);
      })
      .matches(HANKAKU_REGEXP, {
        message: 'メールアドレスは半角文字で入力してください',
        excludeEmptyString: true,
      }),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid, errorMessages },
    getValues,
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const { showToast } = useToast();
  const navigate = useNavigate();

  const {
    fetch,
    isFetching,
    error: serverError,
    status,
    reset: resetFetch,
  } = useFetch<null, ServerError>(
    'POST',
    '/api/school/parents/password_change_tokens/create_v2',
  );

  const [inputtedEmail, setInputtedEmail] = useState('');

  useEffect(() => {
    if (status === 201) {
      navigate('/forgot/password/confirmation', {
        state: { email: inputtedEmail } satisfies EmailConfirmationState,
      });
      showToast(`${inputtedEmail} にメールを送信しました`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigateを入れると2回発火するため
  }, [intl, showToast, inputtedEmail, status]);

  const onSubmit = (body: FormValues) => {
    setInputtedEmail(getValues().email);
    void fetch({ body });
  };

  const inputDisabled = isSubmitting || isFetching;
  const buttonDisabled = !isDirty || !isValid || isSubmitting || isFetching;

  const emailErrorMessages = [
    ...(errorMessages.email || []),
    ...(serverError ? [serverError.message] : []),
  ];

  // メールアドレス変更時にサーバーエラーをクリア
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (serverError) {
      resetFetch();
    }
    void registerWithInnerRef('email').onChange(e);
  };

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      className={styles.root}
    >
      <FormGroup>
        <label htmlFor={inputId} className={styles.inputLabel}>
          メールアドレス
        </label>
        <Field>
          <Input
            {...registerWithInnerRef('email')}
            onChange={handleEmailChange}
            disabled={inputDisabled}
            placeholder="メールアドレスを入力"
            autoComplete="email"
            id={inputId}
          />
          <ErrorList errors={emailErrorMessages} />
        </Field>
      </FormGroup>
      <Button
        type="submit"
        color="primary"
        disabled={buttonDisabled}
        className={styles.submitButton}
      >
        送信
      </Button>
    </form>
  );
};

export default ForgotPasswordForm;
